import { useCallback, useMemo } from 'react';
import { doesExist } from 'utils/tsHelpers';
import { useRequirementMinCountState } from './useRequirementMinCountState';
import { useRequirementMaxCountState } from './useRequirementMaxCountState';
import { CountFilter, CountFilterType } from '../../types';
import { getCountFilterType } from '../../utils/getCountFilterType';

/* custom hook to control the count filter input on the requirement level (any, between exactly)
 * - countFilterType is not submitted (not part of the query)
 * - CountFilterType.ANY: neither minMatch nor maxMatch exist (default, same as not selecting an option) (neither minMatch nor maxMatch fields are visible)
 * - CountFilterType.BETWEEN: minMatch and maxMatch exist, and can have different values (both minMatch and maxMatch fields are visible)
 * - CountFilterType.EXACTLY: minMatch and maxMatch exist, and have the same value (only minMatch field is visible)
 * */

export const useRequirementCountFilterState = () => {
  const { requirementMinCount, setRequirementMinCount } = useRequirementMinCountState();
  const { requirementMaxCount, setRequirementMaxCount } = useRequirementMaxCountState();

  const countFilterType = useMemo(
    () => getCountFilterType(requirementMaxCount, requirementMinCount),
    [requirementMaxCount, requirementMinCount],
  );

  const countFilter = useMemo(
    () => ({
      minCount: requirementMinCount,
      maxCount: requirementMaxCount,
    }),
    [requirementMinCount, requirementMaxCount],
  );

  const setCountFilterType = (updatedCountFilterType: CountFilterType) => {
    if (updatedCountFilterType === CountFilterType.ANY) {
      setRequirementMinCount(undefined);
      setRequirementMaxCount(undefined);
    } else if (updatedCountFilterType === CountFilterType.EXACTLY) {
      setRequirementMinCount(doesExist(requirementMinCount) ? requirementMinCount : 1);
      setRequirementMaxCount(doesExist(requirementMinCount) ? requirementMinCount : 1);
    } else if (updatedCountFilterType === CountFilterType.BETWEEN) {
      setRequirementMinCount(doesExist(requirementMinCount) ? requirementMinCount : 1);
      setRequirementMaxCount(undefined);
    }
  };

  const setCountFilter = useCallback(
    ({ minCount: newMinCount, maxCount: newMaxCount }: CountFilter) => {
      if (countFilterType === CountFilterType.EXACTLY) {
        setRequirementMinCount(newMinCount);
        setRequirementMaxCount(newMinCount);
      } else if (countFilterType === CountFilterType.BETWEEN) {
        setRequirementMinCount(newMinCount);
        setRequirementMaxCount(newMaxCount);
      }
    },
    [],
  );

  return {
    countFilter,
    setCountFilter,
    countFilterType,
    setCountFilterType,
  };
};
